import React, { useState, useEffect, useRef, useMemo } from 'react';

interface StatusOption {
  value: string;
  label: string;
}

interface Props {
  selectedValues: string[];
  onChange: (selectedValues: string[]) => void;
  placeholder?: string;
  className?: string;
}

const STATUS_OPTIONS: StatusOption[] = [
  { value: 'verified', label: '✅ Verified' },
  { value: 'unverified', label: '⭕ Unverified' },
  { value: 'auto-categorized', label: '🤖 Auto-categorized' }
];

export const MultiSelectStatusDropdown: React.FC<Props> = ({
  selectedValues,
  onChange,
  placeholder = "Filter by status...",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [openUpward, setOpenUpward] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<(HTMLDivElement | null)[]>([]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return STATUS_OPTIONS;
    
    const searchLower = searchTerm.toLowerCase();
    return STATUS_OPTIONS.filter(option =>
      option.label.toLowerCase().includes(searchLower) ||
      option.value.toLowerCase().includes(searchLower)
    );
  }, [searchTerm]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check dropdown position
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      if (spaceBelow < 200 && spaceAbove > spaceBelow) {
        setOpenUpward(true);
      } else {
        setOpenUpward(false);
      }
    }
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggleOption = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        break;
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleToggleOption(filteredOptions[highlightedIndex].value);
        }
        break;
    }
  };

  // Get display text for the input
  const getDisplayText = () => {
    if (selectedValues.length === 0) {
      return '';
    }

    const selectedOptions = STATUS_OPTIONS.filter(option => 
      selectedValues.includes(option.value)
    );
    
    if (selectedValues.length <= 2) {
      return selectedOptions.map(option => option.label).join(', ');
    }

    const firstTwo = selectedOptions.slice(0, 2).map(option => option.label);
    const remaining = selectedValues.length - 2;
    return `${firstTwo.join(', ')} +${remaining} more`;
  };

  const getPlaceholder = () => {
    return selectedValues.length > 0 ? '' : placeholder;
  };

  return (
    <div className={`multi-select-filter ${className}`} ref={dropdownRef}>
      <div className={`filter-input-container ${isOpen ? 'open' : ''}`}>
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : getDisplayText()}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setHighlightedIndex(0);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => {
            setIsOpen(true);
            setSearchTerm('');
          }}
          onKeyDown={handleKeyDown}
          placeholder={getPlaceholder()}
          className="filter-input"
          autoComplete="off"
        />
        <div 
          className="filter-arrow"
          onClick={() => {
            setIsOpen(!isOpen);
            if (!isOpen) {
              inputRef.current?.focus();
            }
          }}
        >
          ▼
        </div>
      </div>

      {isOpen && (
        <div 
          className={`filter-dropdown ${openUpward ? 'upward' : ''}`}
          style={{
            maxHeight: '250px',
            overflowY: 'auto'
          }}
        >
          {filteredOptions.length === 0 ? (
            <div className="no-options">No options found</div>
          ) : (
            filteredOptions.map((option, index) => (
              <div
                key={option.value}
                ref={el => optionsRef.current[index] = el}
                className={`filter-option ${highlightedIndex === index ? 'highlighted' : ''} ${selectedValues.includes(option.value) ? 'selected' : ''}`}
                onClick={() => handleToggleOption(option.value)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.value)}
                  onChange={() => {}} // Handled by parent click
                  className="option-checkbox"
                />
                <span className="option-text">{option.label}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
